import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { AuthorName } from '@/components/AuditDate';
import { useFilterNames } from '@/components/exports/useFilterNames';
import { ExportAction, ExportFiltersLabel, ExportStatusBadge } from '@/components/exports/ExportParts';
import { EXPORT_POLL_MS } from '@/components/exports/exports';
import { ReferenceOptions } from '@/components/form/ReferenceOptions';
import { FilterSelect } from '@/components/list/FilterSelect';
import { ListStatus } from '@/components/list/ListStatus';
import { ListToolbar } from '@/components/list/ListToolbar';
import { NoCompanySelected } from '@/components/list/NoCompanySelected';
import { Panel } from '@/components/Panel';
import { Pagination } from '@/components/table/Pagination';
import { useListControls } from '@/hooks/useListControls';
import { usePagedList } from '@/hooks/usePagedList';
import { DATE_TIME_FORMAT } from '@/lib/dayjs';
import ExportModel from '@/models/entities/ExportModel';
import { COMPANY_ROLE } from '@/models/references/references';
import { useCompanyStore, useCurrentCompany } from '@/stores/useCompanyStore';
import { findReferenceId, useReferenceOptions, useReferences } from '@/stores/useReferenceOptions';
import { buildExportListRequest, DEFAULT_EXPORT_FILTERS } from './exportListQuery';

/**
 * Tous les exports de la société sélectionnée (lien « Voir tous les exports » des Téléchargements) : date, type, filtres
 * de la liste exportée, auteur, statut, Télécharger ou Relancer ; paginés côté API, filtrables par type et par statut,
 * rechargés toutes les EXPORT_POLL_MS tant qu'un export de la page est en cours. Réservé au Dirigeant (les autres : aucun
 * export, sans appel à l'API).
 */
export function ExportsPage() {
  const company = useCurrentCompany();
  const companiesLoaded = useCompanyStore((state) => state.loaded);

  if (companiesLoaded && !company) {
    return <NoCompanySelected />;
  }
  if (company?.companyRole !== COMPANY_ROLE.DIRECTOR) {
    // Pas Dirigeant : aucun export à lire, ni listes de référence à charger
    return (
      <Panel>
        <ListStatus className="p-6" loading={!companiesLoaded} empty="Aucun export." />
      </Panel>
    );
  }

  return <DirectorExports key={company.id} companyId={company.id} />;
}

/** Les exports de la société du Dirigeant (une instance par société : filtres et page remis à zéro) */
function DirectorExports({ companyId }: { companyId: number }) {
  const list = useListControls({ filters: DEFAULT_EXPORT_FILTERS });
  const { filters, page, itemsPerPage, changeFilters } = list;
  const types = useReferenceOptions('exportTypes');
  const statuses = useReferenceOptions('exportStatuses');
  const request = useMemo(
    () =>
      buildExportListRequest(
        companyId,
        filters,
        page,
        itemsPerPage,
        (reference) => findReferenceId(types, reference),
        (reference) => findReferenceId(statuses, reference),
      ),
    [companyId, filters, page, itemsPerPage, types, statuses],
  );
  useReferences('timeSheetTypes');
  const [reloadKey, setReloadKey] = useState(0);
  const { models: exports, totalItems, loading, error } = usePagedList(ExportModel, request, reloadKey);
  const names = useFilterNames(exports);
  const running = exports.some((model) => model.isRunning);

  // Suivi des exports en cours de la page
  useEffect(() => {
    if (!running || loading) return;
    const timer = setTimeout(() => setReloadKey((key) => key + 1), EXPORT_POLL_MS);
    return () => clearTimeout(timer);
  }, [running, loading, exports]);

  return (
    <Panel className="overflow-hidden">
      <ListToolbar search={<div className="flex-1" />}>
        <FilterSelect
          label="Filtrer par type"
          allLabel="Tous les types"
          value={filters.exportType}
          onChange={(exportType) => changeFilters({ exportType })}
        >
          <ReferenceOptions name="exportTypes" />
        </FilterSelect>
        <FilterSelect
          label="Filtrer par statut"
          allLabel="Tous les statuts"
          value={filters.exportStatus}
          onChange={(exportStatus) => changeFilters({ exportStatus })}
        >
          <ReferenceOptions name="exportStatuses" />
        </FilterSelect>
      </ListToolbar>

      <div className="overflow-x-auto">
        <table
          className={clsx('w-full text-left border-collapse transition-opacity', loading && !running && 'opacity-60')}
          aria-busy={loading}
        >
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <th className="px-6 py-4">Date</th>
              <th className="px-6 py-4">Type</th>
              <th className="px-6 py-4">Filtres</th>
              <th className="px-6 py-4">Créé par</th>
              <th className="px-6 py-4">Statut</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {exports.map((model) => (
              <tr key={model.id} className="hover:bg-slate-50/80 transition-colors" data-testid="export-row">
                <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                  {model.createdAt?.format(DATE_TIME_FORMAT)}
                </td>
                <td className="px-6 py-4 font-medium text-slate-900">{model.exportTypeLabel}</td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  <ExportFiltersLabel model={model} names={names} />
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  <AuthorName by={model.createdBy} />
                </td>
                <td className="px-6 py-4">
                  <ExportStatusBadge model={model} />
                </td>
                <td className="px-6 py-4 text-right">
                  <ExportAction model={model} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ListStatus
          className="p-6"
          loading={!!request && loading && exports.length === 0}
          error={error}
          empty={
            (!request || (!loading && exports.length === 0)) &&
            (filters.exportType || filters.exportStatus ? 'Aucun export pour ces filtres.' : 'Aucun export.')
          }
        />
      </div>

      {request && <Pagination {...list.pagination} totalItems={totalItems} label={['export', 'exports']} />}
    </Panel>
  );
}
